import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';

import {
    getServerLocalTranslation
} from '../../../core/constants/app.constant';
import { StorageService } from '../../../core/services/storage.service';
import { ENVIRONMENT } from '../../../../environment/environment';
import {
    SurveyChoicesModelInterface,
    SurveyChoicesApiResponseInterface,
    SurveyChoicesApiRequestInterface,
    SurveyOption,
    SurveyProgramTypes
}
from '../../../features/user-personalization-survey/user-personalization-survey.model';
import { ApiResponse } from '../../models/api-response.model';

@Injectable({
    providedIn: 'root',
})
export class SurveyService {
    apiRoute = `${ENVIRONMENT.apiDomain}/api/v1/user-personalization`;

    constructor(
        private httpClient: HttpClient,
        private storageService: StorageService,
        private translateService: TranslateService
    ) {}

    public getSurveyChoices(): Observable<ApiResponse<SurveyChoicesApiResponseInterface>> {
        const defaultLang = this.storageService.language ?? 'en-us';
        const serverLang = getServerLocalTranslation(defaultLang) ?? 'en';

        return this.httpClient.get<ApiResponse<SurveyChoicesApiResponseInterface>>(
            `${this.apiRoute}/choices/all/${serverLang}`
        ).pipe(
            map((response) => {
                return response;
            })
        );
    }

    public getUserSurveyResponses(): Observable<ApiResponse<SurveyChoicesApiResponseInterface>> {
        const defaultLang = this.storageService.language ?? 'en-us';
        const serverLang = getServerLocalTranslation(defaultLang) ?? 'en';

        return this.httpClient.get<ApiResponse<SurveyChoicesApiResponseInterface>>(
            `${this.apiRoute}/choices/user/${serverLang}`
        );
    }

    public submitSurvey(
        surveyData: SurveyChoicesModelInterface,
        userSurveyData: SurveyChoicesModelInterface,
        markAsComplete: boolean,
        forceError: boolean = false
    ): Observable<ApiResponse<boolean>> {
        const filteredProgramCategoryIds: string[] = userSurveyData.programCategories.map(category => category.id);

        const filtereProgramTypeIds: SurveyProgramTypes[] = 
            this.determineProgramTypesForSubmission(userSurveyData.programTypeNames)

        const filteredGoalIds: string[] = userSurveyData.goals.map(goal => goal.id);

        const submissionData: SurveyChoicesApiRequestInterface = {
            programCategoryIds: filteredProgramCategoryIds,
            preferredLanguages: userSurveyData.preferredLanguageDisplayTexts.map(
                translatedLanguage => {
                    let index = surveyData.preferredLanguageDisplayTexts.indexOf(translatedLanguage);
                    return surveyData.preferredLanguages[index];
                }
            ),
            programTypes: filtereProgramTypeIds,
            goalIds: filteredGoalIds,
            markAsComplete: markAsComplete,
            forceError: forceError
        };

        // Debug Log (Front-End)
        // console.log("[On Survey Completed] Data to Submit: ", submissionData);

        return this.httpClient
            .post<ApiResponse<boolean>>(`${this.apiRoute}`, submissionData)
            .pipe(
                map((response) => {
                    return (response.result || {}) as ApiResponse<boolean>;
                })
            );
    }

    private determineProgramTypesForSubmission(programs: string[]): SurveyProgramTypes[] {
        return programs.map(typeName => {
            // Map display names back to programTypeId/webinarTypeId
            const courseTranslation = this.translateService.instant('enum.uiProgramType.course');
            const liveWebinarTranslation = this.translateService.instant('enum.uiProgramType.liveWebinar');
            const pastWebinarTranslation = this.translateService.instant('enum.uiProgramType.pastWebinar');
            
            switch (typeName) {
                case liveWebinarTranslation:
                    return { programTypeId: 1, webinarTypeId: 0 };
                case pastWebinarTranslation:
                    return { programTypeId: 1, webinarTypeId: 1 };
                case courseTranslation:
                default:
                    return { programTypeId: 0, webinarTypeId: 0 };
            }
        });
    }
}
